import React, { useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import {
  AppFileUploader,
  AppModal,
  AppNotificationAlert,
  AppRemixIcon,
  AppSingleSelect,
  AppDeleteModal,
} from '@/layout/AppComponents';
import axios from 'axios';
import { Formik } from 'formik';
import ReactDOMServer from 'react-dom/server';
import { ProgressBar } from 'react-bootstrap';
import styled from 'styled-components';
import colors from '../../../../../../constants/colors';
import AppLink from '../../../../../../routing/helpers/AppLink';
import { axiosPath } from '../../../../../../routing/helpers/router';
import useIsMounted from '../../../../../components/hooks/useIsMounted';
import errorHandler from '@/layout/components/error/ErrorHandler';
import minions from '../../../../../../minions/minions';
import Translator from '../../../../../components/helpers/Translator';
import PreventNavigation from '../../../../../../layout/components/helpers/PreventNavigation';

export default function ResourceTemplate({ template, loadResourceTemplates }) {
  const { moduleBridgeHex } = useParams();
  const dispatch = useDispatch();
  const isMounted = useIsMounted();
  const global = useSelector((state) => state.global);
  const [uploadData, setUploadData] = useState();
  const [batchLoop, setBatchLoop] = useState();
  const [showLoopModal, setShowLoopModal] = useState();
  const [showDownloadModal, setShowDownloadModal] = useState();
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  const loadTemplate = useCallback(
    async () =>
      axios
        .post(
          axiosPath(
            "api_r_user_module_resourceManager_settings_resourceTemplate_get"
          ),
          {
            moduleBridgeHex,
            templateHex: template.hex,
          }
        )
        .then(({ data }) => {
          if (isMounted.current) setUploadData(data);
        })
        .catch((e) => dispatch(errorHandler(e))),
    [moduleBridgeHex, template]
  );

  // > DELETE TEMPLATE
  const deleteTemplate = useCallback(
    async (templateHex) =>
      axios
        .post(
          axiosPath(
            "api_r_user_module_resourceManager_settings_resourceTemplate_delete"
          ),
          {
            moduleBridgeHex,
            templateHex,
          }
        )
        .then(() => {
          if (isMounted.current) loadResourceTemplates();
        })
        .catch((e) => dispatch(errorHandler(e))),
    [moduleBridgeHex]
  );
  // < DELETE TEMPLATE

  const uploadXls = useCallback(async () => {
    let values = {
      moduleBridgeHex,
      templateHex: template.hex,
      tempBatchLoopID: batchLoop?.tempBatchLoopID || null,
    };

    if (!batchLoop?.tempBatchLoopID) {
      values = {
        ...values,
        ...batchLoop,
      };
    }

    axios
      .post(
        axiosPath("api_r_user_module_resourceManager_xls_upload"),
        minions.forms.toFormData(values),
        { headers: { "Content-Type": "multipart/form-data" } }
      )
      .then(({ data }) => {
        if (isMounted.current) {
          if (data?.error) {
            AppNotificationAlert({
              title: ReactDOMServer.renderToString(
                <Translator file="user" name="user.error" />
              ),
              message: ReactDOMServer.renderToString(
                <Translator
                  file="module-resourceManager"
                  name="resource.error-on-line-data"
                  variables={{
                    dataError: data.error,
                  }}
                />
              ),
              type: "danger",
              duration: 5000,
            });
            setBatchLoop(undefined);
            setUploadData(undefined);
            setShowLoopModal(undefined);
          } else {
            setBatchLoop(data);
          }
        }
      })
      .catch((e) => dispatch(errorHandler(e)));
  }, [moduleBridgeHex, template, batchLoop]);

  useEffect(() => {
    if (batchLoop && !batchLoop?.isFinished) {
      if (!showLoopModal) setShowLoopModal(true);
      uploadXls();
    } else if (batchLoop?.isFinished) {
      setBatchLoop(undefined);
      setShowLoopModal(undefined);
      setUploadData(undefined);
      setShowDownloadModal(undefined);

      if (batchLoop?.url) {
        setShowDownloadModal(batchLoop?.url);
      } else {
        AppNotificationAlert({
          title: ReactDOMServer.renderToString(
            <Translator
              file="module-resourceManager"
              name="resource.import.completed"
            />
          ),
          message: ReactDOMServer.renderToString(
            <Translator
              file="module-resourceManager"
              name="resource.all.assets.imported.correctly"
            />
          ),
          type: 'success',
          duration: 25000,
        });
      }
    } else if (batchLoop?.url) {
      setBatchLoop(undefined);
      setShowLoopModal(undefined);
      setUploadData(undefined);
    }
  }, [batchLoop]);

  const handleXlsDownload = useCallback(
    async (templateHex) =>
      axios
        .post(axiosPath('api_r_user_module_resourceManager_xls_download'), {
          moduleBridgeHex,
          templateHex,
        })
        .then(({ data }) => (window.location = data))
        .catch((e) => dispatch(errorHandler(e))),
    [moduleBridgeHex, template],
  );

  return (
    <tr key={template.hex}>
      <td>{template.name}</td>
      <td>{template.category}</td>
      <td>{template.subcategory}</td>
      <td>
        <span className="badge badge-pill badge-success">{template.uses}</span>
      </td>
      <OptionBlock>
        <AppLink
          path="user_module_resourceManager_settings_resourceTemplate_add"
          params={{ moduleBridgeHex, templateHex: template.hex }}
          title={ReactDOMServer.renderToString(
            <Translator
              file="module-resourceManager"
              name="resource.add.asset"
            />,
          )}
        >
          <AppRemixIcon icon="file-add" color={colors.primary} size={16} />
        </AppLink>
        <AppLink
          path="user_module_resourceManager_settings_resourceTemplate_clone"
          params={{
            moduleBridgeHex,
            templateHex: template.hex,
          }}
          title={ReactDOMServer.renderToString(
            <Translator
              file="module-resourceManager"
              name="resource.clone.template"
            />,
          )}
        >
          <AppRemixIcon icon="file-copy" color={colors.primary} size={16} />
        </AppLink>

        <span
          onClick={() => handleXlsDownload(template.hex)}
          // href={`${global.urlData.userApiUrl}m/resource-manager/xls/download/${moduleBridgeHex}/${template.hex}/`} 23-01-2026
          title={ReactDOMServer.renderToString(
            <Translator
              file="module-resourceManager"
              name="resource.download.asset.import.template"
            />,
          )}
          className="ml-2"
        >
          <AppRemixIcon icon="file-excel" />
        </span>
        <span
          onClick={loadTemplate}
          title={ReactDOMServer.renderToString(
            <Translator
              file="module-resourceManager"
              name="resource.import.assets"
            />,
          )}
          className="ml-2 pointer"
        >
          <AppRemixIcon icon="upload" />
        </span>
        <AppRemixIcon
          icon="delete-bin"
          color={
            !template.fixed && template.uses === 0
              ? colors.danger
              : colors.grey400
          }
          size={16}
          className={!template.fixed && template.uses === 0 ? 'pointer' : ''}
          onClick={() =>
            !template.fixed && template.uses === 0 && setShowDeleteModal(true)
          }
        />
        {showDeleteModal && (
          <AppDeleteModal
            show={showDeleteModal}
            title={
              <Translator
                file="module-resourceManager"
                name="resource.delete.template"
              />
            }
            onClose={() => setShowDeleteModal(false)}
            onConfirm={() => {
              deleteTemplate(template.hex);
            }}
            text={
              <p>
                <Translator
                  file="module-resourceManager"
                  name="resource.want.delete.template"
                />
                <br />
                <b>
                  <Translator
                    file="module-resourceManager"
                    name="resource.action.cannot.undone"
                  />
                  .
                </b>
                <br />
                <br />
                <Translator
                  file="module-resourceManager"
                  name="resource.assets.generated.template.not.deleted.lose.grouping"
                />
              </p>
            }
          />
        )}
        <AppModal
          title={
            <Translator
              file="module-resourceManager"
              name="resource.asset.importer"
            />
          }
          show={!!uploadData && batchLoop === undefined}
          onClose={() => setUploadData(undefined)}
        >
          <Formik
            initialValues={{
              importFile: null,
              plant: null,
              files: [],
            }}
            onSubmit={setBatchLoop}
          >
            {({ setFieldValue, handleSubmit, values, errors }) => (
              <form onSubmit={handleSubmit}>
                <AppFileUploader
                  id={1}
                  placeholder={
                    values.importFile
                      ? values?.importFile[0]?.name
                      : ReactDOMServer.renderToString(
                          <Translator
                            file="module-resourceManager"
                            name="resource.select-a-file"
                          />,
                        )
                  }
                  onChange={(opt) => setFieldValue('importFile', opt)}
                />
                <AppSingleSelect
                  placeholder={
                    <Translator
                      file="module-resourceManager"
                      name="resource.select.installation"
                    />
                  }
                  options={uploadData?.plants}
                  icon={<AppRemixIcon icon="building-4" />}
                  onChange={(value) => setFieldValue('plant', value)}
                  value={values.plant}
                  error={errors.plant}
                  hasSearchBox
                />
                <AppFileUploader
                  id={2}
                  isMulti
                  placeholder={
                    values.files.length === 0 ? (
                      <Translator
                        file="module-resourceManager"
                        name="resource.select-one-more-files"
                      />
                    ) : (
                      <Translator
                        file="module-resourceManager"
                        name="resource.elements-selected-elements"
                        variables={{ elements: values.files.length }}
                      />
                    )
                  }
                  onChange={(opt) => setFieldValue('files', opt)}
                />
                <button type="submit" className="btn btn-sm btn-primary">
                  <Translator
                    file="module-resourceManager"
                    name="resource.import"
                  />
                </button>
              </form>
            )}
          </Formik>
        </AppModal>
        <AppModal
          show={showLoopModal}
          title={ReactDOMServer.renderToString(
            <Translator
              file="module-resourceManager"
              name="resource.mass-generation"
            />,
          )}
          isCloseButton={false}
        >
          <>
            {!batchLoop?.isFinished && <PreventNavigation />}

            <h4>
              {batchLoop?.modalInfo?.message || (
                <Translator
                  file="module-resourceManager"
                  name="resource.precalculating"
                />
              )}
            </h4>
            <ProgressBar
              animated
              now={batchLoop?.modalInfo?.percent || 0}
              label={`${parseInt(batchLoop?.modalInfo?.percent || 0, 10)}%`}
            />
          </>
        </AppModal>
        <AppModal
          show={showDownloadModal}
          title=""
          onClose={() => setShowDownloadModal(undefined)}
        >
          <>
            <p className="mb-4">
              <Translator
                file="module-resourceManager"
                name="resource.import.contains.errors.download.file.view"
              />
            </p>
            <a
              href={showDownloadModal}
              target="_blank"
              rel="noreferrer"
              className="btn btn-info"
              onClick={() =>
                setTimeout(() => setShowDownloadModal(undefined), 100)
              }
            >
              <Translator
                file="module-resourceManager"
                name="resource.download"
              />
            </a>
          </>
        </AppModal>
      </OptionBlock>
    </tr>
  );
}

const OptionBlock = styled.td`
  display: flex;
  gap: 10px;
  a {
    &:hover,
    &:focus {
      text-decoration: none;
    }
  }
`;
